import { dbManager } from './IndexedDBManager';

interface AudioChunkSession {
    id: string;
    chunks: Blob[];
    startTime: number;
    lastSaveTime: number;
    isRecording: boolean;
    isTranscribing: boolean;
    duration: number;
}

type CurrentSession = AudioChunkSession | null;

const CHUNK_SAVE_INTERVAL = 30000; // Save every 30 seconds
const STORE_NAME = 'audio-chunks';
const SESSION_KEY = 'current-recording-session';

class AudioChunkManager {
    private currentSession: CurrentSession = null;
    private saveInterval: NodeJS.Timeout | null = null;

    constructor() {
        // Initialize recovery on page load
        this.initializeRecovery();
        // Add refresh protection
        this.setupRefreshProtection();
    }

    private setupRefreshProtection() {
        window.addEventListener('beforeunload', (e) => {
            if (this.currentSession?.isRecording || this.currentSession?.isTranscribing) {
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
        });
    }

    private async initializeRecovery() {
        const savedSession = sessionStorage.getItem(SESSION_KEY);
        if (savedSession) {
            const session = JSON.parse(savedSession);
            const chunks = await dbManager.getAllChunks(session.id);
            if (chunks.length > 0) {
                this.currentSession = {
                    ...session,
                    chunks: chunks.map(chunk => new Blob([chunk.data], { type: 'audio/webm' }))
                };
            }
        }
    }

    startRecording(sessionId: string) {
        this.currentSession = {
            id: sessionId,
            chunks: [],
            startTime: Date.now(),
            lastSaveTime: Date.now(),
            isRecording: true,
            isTranscribing: false,
            duration: 0
        };
        
        // Start periodic saving
        this.saveInterval = setInterval(() => {
            this.saveCurrentChunks();
        }, CHUNK_SAVE_INTERVAL);

        // Save session state
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(this.currentSession));
    }

    addChunk(chunk: Blob) {
        if (!this.currentSession) return;
        
        this.currentSession.chunks.push(chunk);
        this.currentSession.duration = Date.now() - this.currentSession.startTime;
        
        // Save immediately if enough time has passed
        if (Date.now() - this.currentSession.lastSaveTime >= CHUNK_SAVE_INTERVAL) {
            this.saveCurrentChunks();
        }
    }

    private async saveCurrentChunks() {
        if (!this.currentSession) return;

        const unsavedChunks = this.currentSession.chunks.slice(
            this.getLastSavedChunkIndex()
        );

        for (const chunk of unsavedChunks) {
            const arrayBuffer = await chunk.arrayBuffer();
            await dbManager.saveAudioChunk(
                this.currentSession.id,
                new Uint8Array(arrayBuffer),
                Date.now()
            );
        }

        this.currentSession.lastSaveTime = Date.now();
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(this.currentSession));
    }

    private getLastSavedChunkIndex(): number {
        if (!this.currentSession) return 0;
        
        const timeSinceLastSave = Date.now() - this.currentSession.lastSaveTime;
        const chunksToKeep = Math.ceil(timeSinceLastSave / CHUNK_SAVE_INTERVAL);
        return Math.max(0, this.currentSession.chunks.length - chunksToKeep);
    }

    async stopRecording(): Promise<Blob> {
        if (!this.currentSession) throw new Error('No active recording session');

        // Save any remaining chunks
        await this.saveCurrentChunks();

        // Combine all chunks into a single blob
        const finalBlob = new Blob(this.currentSession.chunks, {
            type: 'audio/webm'
        });

        // Clear the interval and session
        if (this.saveInterval) {
            clearInterval(this.saveInterval);
            this.saveInterval = null;
        }

        this.currentSession.isRecording = false;
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(this.currentSession));

        return finalBlob;
    }

    startTranscribing() {
        if (!this.currentSession) return;
        this.currentSession.isTranscribing = true;
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(this.currentSession));
    }

    stopTranscribing() {
        if (!this.currentSession) return;
        this.currentSession.isTranscribing = false;
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(this.currentSession));
    }

    async clearSession() {
        if (this.currentSession) {
            await dbManager.deleteAudioChunks(this.currentSession.id);
            this.currentSession = null;
            sessionStorage.removeItem(SESSION_KEY);
        }
    }

    getSessionState(): { isRecording: boolean; isTranscribing: boolean } {
        return {
            isRecording: this.currentSession?.isRecording || false,
            isTranscribing: this.currentSession?.isTranscribing || false
        };
    }

    getCurrentSession(): CurrentSession {
        return this.currentSession;
    }
}

export const audioChunkManager = new AudioChunkManager();
